import { Box, Typography } from '@mui/material';
import {
  TournamentBracketMatchGameEntity,
  TournamentBracketResult,
  TournamentStatus,
} from '@okletsplay/core';
import { BracketSeedEntity } from 'app/interfaces/entity';
import classNames from 'classnames';
import { BracketSeedStatus } from 'modules/bracket/components/BracketSeedStatus';
import { ACTIVE_MATCH_STATUSES } from 'modules/bracket/constants';
import React, { FC, useMemo } from 'react';
import { BestOfLabel } from 'shared/components/BestOfLabel';
import { TeamVsTeam } from 'shared/components/TeamVsTeam';
import { TeamVsTeamEntity } from 'shared/interfaces';
import { useStyles } from './TournamentMatchDetailsHeader.styles';
import { getTeamCaptain } from 'shared/utils/get-team-captain';

interface TournamentMatchDetailsHeaderProps {
  seed: BracketSeedEntity | null;
  tournamentStatus: TournamentStatus;
}

export const TournamentMatchDetailsHeader: FC<TournamentMatchDetailsHeaderProps> = ({
  seed,
  tournamentStatus,
}) => {
  const { classes } = useStyles();

  const scores = useMemo(() => {
    const matchGameResults =
      seed?.matchGames?.flatMap(
        (matchGame: TournamentBracketMatchGameEntity) => matchGame?.matchGameResults ?? []
      ) ?? [];

    return matchGameResults.reduce((acc: Record<string, number>, currentValue) => {
      const key = currentValue.opponentId as string;

      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += currentValue?.score ?? 0;

      return acc;
    }, {});
  }, [seed?.matchGames]);

  const matchResults: Record<string, TournamentBracketResult> = useMemo(() => {
    if (seed?.status && ACTIVE_MATCH_STATUSES.includes(seed.status)) {
      return {};
    }

    const maxScore = Math.max(...Object.values(scores));
    const results: Record<string, TournamentBracketResult> = {};
    if (!maxScore) {
      return results;
    }

    for (const key of Object.keys(scores)) {
      results[key] =
        maxScore === scores[key] ? TournamentBracketResult.Win : TournamentBracketResult.Loss;
    }

    return results;
  }, [scores, seed?.status]);

  const teams: TeamVsTeamEntity[] = useMemo(() => {
    if (!seed) {
      return [];
    }

    return seed.teams.map((team): TeamVsTeamEntity => {
      const captain = getTeamCaptain(team);

      return {
        name: team?.name ?? '',
        captain: captain?.user,
        status: matchResults?.[team.id],
      };
    });
  }, [matchResults, seed]);

  if (!seed) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Typography className={classes.section} component="span">
        <BracketSeedStatus matchStatus={seed.status} tournamentStatus={tournamentStatus} />
      </Typography>
      <Typography className={classNames(classes.bestOf, classes.section)}>
        <BestOfLabel round={seed.round} gamesCount={seed?.matchGames?.length} />
      </Typography>
      <TeamVsTeam teams={teams} score={Object.values(scores)} />
    </Box>
  );
};
